import { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import type { MessageKey } from "@expertloop/i18n";
import type {
	AnalyticsTransactionMovement,
	AnalyticsTransactionRow,
	AnalyticsTransactionsPage,
	OrderStatus,
} from "@expertloop/shared";

interface TransactionsTableProps {
	page: AnalyticsTransactionsPage;
	onPageChange: (page: number) => void;
}

/** Libellé de l'état d'ensemble d'une commande. */
const ORDER_STATUS_LABELS: Record<OrderStatus, MessageKey> = {
	captured: "adminFinance.orderStatus.captured",
	refund_pending: "adminFinance.orderStatus.refund_pending",
	refunded: "adminFinance.orderStatus.refunded",
	canceled: "adminFinance.orderStatus.canceled",
	expired: "adminFinance.orderStatus.expired",
	authorized: "adminFinance.orderStatus.authorized",
	pending_authorization: "adminFinance.orderStatus.pending_authorization",
	cancellation_requested: "adminFinance.orderStatus.cancellation_requested",
	settled_free: "adminFinance.orderStatus.settled_free",
};

/**
 * Commandes de la période, une ligne par commande (BL-F8).
 *
 * Une commande occupe une seule ligne, qui porte son montant encaissé, sa
 * devise et son état d'ensemble. Ses mouvements — l'encaissement, puis le
 * remboursement s'il a abouti — se déplient sous elle, chacun gardant sa propre
 * date : réunir les mouvements ne doit pas laisser croire qu'ils appartiennent
 * tous au même mois. Le total de la période reste calculé sur la base choisie,
 * et aucun chiffre n'est recalculé ici.
 */
export function TransactionsTable({ page, onPageChange }: TransactionsTableProps) {
	const { t } = useI18n();
	const [expanded, setExpanded] = useState<Record<string, boolean>>({});

	const isRefunds = page.basis === "refund";

	if (page.rows.length === 0) {
		return (
			<p className="admin-overview__state">
				{t(isRefunds ? "adminFinance.noRefunds" : "adminFinance.noTransactions")}
			</p>
		);
	}

	const toggle = (orderId: string) => {
		setExpanded((current) => ({ ...current, [orderId]: !current[orderId] }));
	};

	return (
		<>
			<p className="admin-finance__count">
				{t("adminFinance.transactionCount", {
					count: page.total,
					plural: page.total !== 1 ? "s" : "",
				})}
			</p>

			<div className="table-container">
				<table>
					<thead>
						<tr>
							{/* La date qui borne la lecture choisie : l'encaissement pour les
							    ventes, l'aboutissement pour les remboursements. */}
							<th>{t(isRefunds ? "adminFinance.columnRefundedAt" : "adminFinance.columnCapturedAt")}</th>
							<th>{t("adminFinance.columnOrder")}</th>
							{/* La devise est une colonne à part entière : fondue dans le format
							    de chaque montant, elle ne pouvait ni se trier ni se lire. */}
							<th>{t("adminFinance.columnCurrency")}</th>
							<th>{t("adminFinance.columnAmount")}</th>
							<th>{t("adminFinance.columnStatus")}</th>
							<th>{t("adminFinance.columnMovements")}</th>
						</tr>
					</thead>
					<tbody>
						{page.rows.map((row) => (
							<OrderRows
								key={row.orderId}
								row={row}
								isRefunds={isRefunds}
								isExpanded={Boolean(expanded[row.orderId])}
								onToggle={() => toggle(row.orderId)}
							/>
						))}
					</tbody>
				</table>
			</div>

			<div className="admin-finance__pagination">
				<button
					type="button"
					className="btn"
					disabled={page.page <= 1}
					onClick={() => onPageChange(page.page - 1)}
				>
					{t("adminFinance.previousPage")}
				</button>
				<span>{t("adminFinance.pageOf", { page: page.page, totalPages: page.totalPages })}</span>
				<button
					type="button"
					className="btn"
					disabled={page.page >= page.totalPages}
					onClick={() => onPageChange(page.page + 1)}
				>
					{t("adminFinance.nextPage")}
				</button>
			</div>
		</>
	);
}

interface OrderRowsProps {
	row: AnalyticsTransactionRow;
	isRefunds: boolean;
	isExpanded: boolean;
	onToggle: () => void;
}

/** La ligne d'une commande, et le détail de ses mouvements quand il est déplié. */
function OrderRows({ row, isRefunds, isExpanded, onToggle }: OrderRowsProps) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();

	// La date de la ligne suit la base choisie ; chaque mouvement garde la sienne
	// dans le détail, ce qui rend visible un remboursement de mars sur une vente
	// de janvier.
	const boundingDate = isRefunds && row.refundedAt ? row.refundedAt : row.capturedAt;
	const detailId = `movements-${row.orderId}`;

	return (
		<>
			<tr>
				<td>{new Date(boundingDate).toLocaleString()}</td>
				<td>
					{/* La conversation est le seul écran d'administration qui montre
					    une commande : c'est donc la destination utile. */}
					<Link className="table-link" to={`/chats/${row.chatId}`}>
						{row.orderId}
					</Link>
				</td>
				<td>{row.currency.toUpperCase()}</td>
				{/* Le montant de la ligne est celui qui a été encaissé : c'est ce que
				    la commande a rapporté, indépendamment de ce qui a été rendu depuis. */}
				<td>{formatCents(row.capturedAmount, row.currency)}</td>
				<td>{t(ORDER_STATUS_LABELS[row.status] ?? "adminFinance.orderStatus.captured")}</td>
				<td>
					<button
						type="button"
						className="admin-finance__disclosure"
						aria-expanded={isExpanded}
						aria-controls={detailId}
						onClick={onToggle}
					>
						{t(isExpanded ? "adminFinance.collapseOrder" : "adminFinance.expandOrder", {
							order: row.orderId,
						})}
						<span className="admin-finance__movement-count">
							{t("adminFinance.movementCount", {
								count: row.movements.length,
								plural: row.movements.length !== 1 ? "s" : "",
							})}
						</span>
					</button>
				</td>
			</tr>
			{isExpanded && (
				<tr id={detailId} className="admin-finance__movements-row">
					<td colSpan={6}>
						<ul className="admin-finance__movements">
							{row.movements.map((movement) => (
								<MovementLine
									key={`${movement.kind}-${movement.occurredAt}`}
									movement={movement}
									currency={row.currency}
								/>
							))}
						</ul>
					</td>
				</tr>
			)}
		</>
	);
}

/**
 * Un mouvement d'argent, avec sa date, son montant et sa commission.
 *
 * Les mentions d'incertitude restent attachées au mouvement qu'elles
 * concernent : une commission reconstituée sur le remboursement ne dit rien de
 * celle qui a été prélevée à la vente, et l'inverse est vrai aussi.
 */
function MovementLine({ movement, currency }: { movement: AnalyticsTransactionMovement; currency: string }) {
	const { t } = useI18n();
	const { formatCents } = useMoneyFormatter();

	const isRefund = movement.kind === "refund";

	return (
		<li className="admin-finance__movement">
			<span className="admin-finance__movement-kind">
				{t(isRefund ? "adminFinance.movementRefund" : "adminFinance.movementCapture")}
			</span>
			<span>
				<span className="admin-finance__movement-label">{t("adminFinance.movementDate")} : </span>
				{new Date(movement.occurredAt).toLocaleString()}
			</span>
			<span>
				<span className="admin-finance__movement-label">{t("adminFinance.movementAmount")} : </span>
				{formatCents(movement.amount, currency)}
			</span>
			<span>
				<span className="admin-finance__movement-label">
					{t(isRefund ? "adminFinance.movementFeeReturned" : "adminFinance.movementFee")} :{" "}
				</span>
				{formatCents(movement.platformFee, currency)}
				{movement.platformFeeIsFallback && (
					<span className="admin-finance__mark"> {t("adminFinance.fallbackMark")}</span>
				)}
			</span>
			{!isRefund && (
				<span>
					<span className="admin-finance__movement-label">{t("adminFinance.columnStripeFee")} : </span>
					{movement.stripeFee === null || movement.stripeFee === undefined ? (
						<span className="admin-finance__mark">{t("adminFinance.notRecorded")}</span>
					) : (
						formatCents(movement.stripeFee, currency)
					)}
				</span>
			)}
		</li>
	);
}
